<script setup>
import {
  BCollapse,
  BDropdownItem,
  BDropdownHeader,
  BNavbar,
  BNavbarBrand,
  BNavItem,
  BNavItemDropdown,
  BNavbarNav,
  BNavbarToggle,
} from 'bootstrap-vue-next';

import Logo from '@images/gitlab_logo.svg';

import { useVueData } from '~/common/useVueData';

import ContributorLevelBadge from './ContributorLevelBadge.vue';
import LevelProgress from './LevelProgress.vue';
import PendingSubmissionsPill from './navigation-menu/PendingSubmissionsPill.vue';
import ThemeSwitcher from './ThemeSwitcher.vue';

const { username, contributorLevel, teamMember, coreTeam, isSignedIn: signedIn, isAdmin: admin } = useVueData();
</script>

<template>
  <BNavbar
    class="navbar-dark"
    toggleable="lg"
  >
    <BNavbarToggle
      target="main-nav-collapse"
    />
    <BNavbarBrand
      tag="h1"
      class="mb-0"
      :to="{ name: 'home' }"
    >
      <img
        class="navbar-brand-logo"
        :src="Logo"
        alt="GitLab logo"
      >
      <span class="navbar-brand-text ms-3">Contributors</span>
    </BNavbarBrand>
    <BCollapse
      id="main-nav-collapse"
      is-nav
    >
      <BNavbarNav>
        <BNavItemDropdown text="Contribute">
          <BDropdownItem :to="{ name: 'issues' }">
            Find Issues
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'manage-issue' }">
            Manage Issue
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'documentation', params: { page: 'user-guide' }, hash: '#translations' }">
            Translation
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'documentation', params: { page: 'user-guide' }, hash: '#discord-messages' }">
            Discord
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'documentation', params: { page: 'user-guide' }, hash: '#forum-posts' }">
            Forum
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'documentation', params: { page: 'user-guide' }, hash: '#events' }">
            Events
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'documentation', params: { page: 'user-guide' }, hash: '#content' }">
            Content
          </BDropdownItem>
        </BNavItemDropdown>
        <BNavItemDropdown text="Leaderboards">
          <BDropdownItem :to="{ name: 'leaderboard' }">
            Individual
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'hackathon' }">
            Hackathon
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'organizations' }">
            Organizations
          </BDropdownItem>
        </BNavItemDropdown>
        <BNavItemDropdown text="Recognition">
          <BDropdownItem :to="{ name: 'rewards' }">
            Rewards
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'notable-contributors' }">
            Notable Contributors
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'top-annual-contributors' }">
            Top Annual Contributors
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'bonus-points' }">
            Bonus Points
          </BDropdownItem>
        </BNavItemDropdown>
        <BNavItemDropdown text="Platform">
          <BDropdownItem :to="{ name: 'documentation', params: { page: 'user-guide' } }">
            User Guide
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'dashboard', query: { period: 'month', chartType: 'bar', stacked: true, unique: false, series: 'merge,ai_catalog' } }">
            Metrics
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'insights' }">
            Insights
          </BDropdownItem>
        </BNavItemDropdown>
        <BNavItemDropdown text="Hackathons">
          <BDropdownItem :to="{ name: 'hackathon-events' }">
            Overview
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'transcend-hackathon' }">
            Transcend Hackathon
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'hackathon' }">
            Community Hackathon
          </BDropdownItem>
        </BNavItemDropdown>
      </BNavbarNav>
      <BNavbarNav class="ms-auto mb-lg-0">
        <ThemeSwitcher />
        <PendingSubmissionsPill v-if="admin" />
        <BNavItemDropdown
          v-if="signedIn"
        >
          <template #button-content>
            <ContributorLevelBadge
              :contributor-level="contributorLevel"
              :badge-size="25"
              :team-member="teamMember"
              :core-team="coreTeam"
            />
            <span class="mx-2">{{ username }}</span>
          </template>
          <BDropdownHeader v-if="!teamMember">
            <div class="mb-2">Level progress</div>
            <LevelProgress minimized />
          </BDropdownHeader>
          <BDropdownItem
            v-if="admin"
            :to="{ name: 'admin' }"
          >
            Admin
          </BDropdownItem>
          <BDropdownItem :to="{ name: 'user', params: { userHandle: username } }">
            Profile
          </BDropdownItem>
          <BDropdownItem :href="`https://gitlab.com/${username}`">
            GitLab profile
          </BDropdownItem>
          <BDropdownItem href="/logout">
            Logout
          </BDropdownItem>
        </BNavItemDropdown>
        <template v-else>
          <BNavItem href="/login">
            Login
          </BNavItem>
        </template>
      </BNavbarNav>
    </BCollapse>
  </BNavbar>
</template>
